import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';
import HubupPremiumQr from '../../components/hubup/HubupPremiumQr';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useUserAccess } from '../../context/UserAccessContext';
import { useHubupCommunities } from '../../context/HubupCommunitiesContext';
import {
  fetchHubupCommunities,
  createHubupAdminGroup,
  updateHubupAdminGroup,
  deleteHubupAdminGroup,
  resolveHubupAssetUrl,
  uploadHubupGroupIcon,
  deleteHubupGroupIcon,
  buildHubupGroupInviteUrl,
} from '../../api/hubup';

function hubupGroupIconImgSrc(groupIconUrl, cacheBust) {
  const base = resolveHubupAssetUrl(groupIconUrl);
  if (!base) return '';
  if (/[?&]v=/.test(base)) return base;
  if (cacheBust == null) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}_cb=${cacheBust}`;
}

const HubupCommunitiesManage = () => {
  const { groupId } = useParams();
  const { user } = useUserAccess();
  const { refetch: refetchMyCommunities } = useHubupCommunities();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState('');
  const [formIsPro, setFormIsPro] = useState(false);
  const [formNotificationLeft, setFormNotificationLeft] = useState('0');
  const [formError, setFormError] = useState(null);
  const [iconBusyId, setIconBusyId] = useState(null);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [groupIconCacheBust, setGroupIconCacheBust] = useState({});
  const [iconError, setIconError] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [copyHint, setCopyHint] = useState('');
  const copyHintTimerRef = useRef(null);

  const isAdmin = user?.permission === 'admin';

  useEffect(() => {
    if (!copyHint) return undefined;
    if (copyHintTimerRef.current) clearTimeout(copyHintTimerRef.current);
    copyHintTimerRef.current = setTimeout(() => {
      setCopyHint('');
      copyHintTimerRef.current = null;
    }, 2200);
    return () => {
      if (copyHintTimerRef.current) clearTimeout(copyHintTimerRef.current);
    };
  }, [copyHint]);

  const copyInviteCode = async (code) => {
    const c = String(code ?? '').trim();
    if (!c) return;
    try {
      await navigator.clipboard.writeText(c);
      setCopyHint('Code d’invitation copié.');
    } catch {
      setCopyHint('Copie impossible (navigateur).');
    }
  };

  const copyInviteLink = async (code) => {
    const url = buildHubupGroupInviteUrl(code);
    if (!url) {
      setCopyHint('Définissez VITE_HUBUP_PUBLIC_ORIGIN pour copier le lien (app HubUP publique).');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopyHint('Lien d’invitation copié.');
    } catch {
      setCopyHint('Copie impossible (navigateur).');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchHubupCommunities();
      setCommunities(res?.data?.communities || []);
    } catch (e) {
      setError("Impossible de charger les communautés.");
      setCommunities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormIsPro(false);
    setFormNotificationLeft('0');
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setFormName(c.name);
    setFormIsPro(c.isPro === true);
    setFormNotificationLeft(String(Math.max(0, Number(c.notificationLeft) || 0)));
    setFormError(null);
    setShowForm(true);
  };

  const afterMutation = async () => {
    await load();
    await refetchMyCommunities();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      const payload = {
        name: formName.trim(),
        isPro: formIsPro,
        notificationLeft: Math.max(
          0,
          Number.parseInt(String(formNotificationLeft), 10) || 0,
        ),
      };
      if (editing) {
        await updateHubupAdminGroup(editing.id, payload);
      } else {
        await createHubupAdminGroup(payload);
      }
      setShowForm(false);
      await afterMutation();
    } catch (err) {
      setFormError('Erreur lors de la sauvegarde.');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer la communauté « ${c.name} » ?`)) return;
    setError(null);
    setDeleteBusyId(c.id);
    try {
      await deleteHubupAdminGroup(c.id);
      await afterMutation();
    } catch (err) {
      setError(err?.message || 'Suppression impossible.');
    } finally {
      setDeleteBusyId(null);
    }
  };

  const handleIconFile = async (c, ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    const maxBytes = 2 * 1024 * 1024;
    if (file.size > maxBytes) {
      setIconError('Image trop volumineuse (max 2 Mo).');
      return;
    }
    setIconError(null);
    setIconBusyId(c.id);
    try {
      await uploadHubupGroupIcon(c.id, file);
      setGroupIconCacheBust((prev) => ({ ...prev, [c.id]: Date.now() }));
      await afterMutation();
    } catch (err) {
      setIconError('Envoi de l’icône impossible. Utilisez une image JPG ou PNG (max 2 Mo).');
    } finally {
      setIconBusyId(null);
    }
  };

  const handleDeleteIcon = async (c) => {
    if (!window.confirm(`Retirer l’icône de « ${c.name} » ?`)) return;
    setIconError(null);
    setIconBusyId(c.id);
    try {
      await deleteHubupGroupIcon(c.id);
      setGroupIconCacheBust((prev) => {
        const next = { ...prev };
        delete next[c.id];
        return next;
      });
      await afterMutation();
    } catch (err) {
      setIconError('Suppression de l’icône impossible.');
    } finally {
      setIconBusyId(null);
    }
  };

  if (!isAdmin) {
    return <Navigate to={`/hubup/g/${groupId}/dashboard`} replace />;
  }

  return (
    <>
      <Helmet>
        <title>Gérer les communautés — HubUP</title>
      </Helmet>
      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
            Gérer les communautés
          </h1>
          {error && (
            <p className="text-xs text-error mb-2">
              {error}
            </p>
          )}
          {iconError && (
            <p className="text-xs text-error mb-2">
              {iconError}
            </p>
          )}
          {copyHint ? (
            <p className="text-xs text-muted-foreground mb-2">
              {copyHint}
            </p>
          ) : null}
        </div>

        <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Liste des communautés
              </h2>
            </div>
            <Button
              onClick={openCreate}
              iconName="Plus"
              iconPosition="left"
              size="lg"
              className="w-full sm:w-auto shrink-0"
            >
              Ajouter une communauté
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-[120px]">
                    Icône
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-20">
                    Pro
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-28">
                    Notif. restantes
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[9rem]">
                    Invitation
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      Chargement…
                    </td>
                  </tr>
                ) : communities.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                      Aucune communauté pour le moment.
                    </td>
                  </tr>
                ) : (
                  communities.map((c) => (
                    <tr key={c.id} className="hover:bg-muted/50 transition-smooth">
                      <td className="px-4 py-3">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="sr-only"
                          id={`hubup-icon-${c.id}`}
                          disabled={iconBusyId === c.id}
                          onChange={(ev) => handleIconFile(c, ev)}
                        />
                        <div className="relative inline-block align-middle">
                          <label
                            htmlFor={`hubup-icon-${c.id}`}
                            title="Changer l’icône"
                            className={`group block w-10 h-10 rounded-md border border-border bg-muted/50 overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/40 transition-smooth ${
                              iconBusyId === c.id
                                ? 'pointer-events-none opacity-60 cursor-wait ring-0'
                                : ''
                            }`}
                          >
                            <span className="sr-only">
                              Changer l’icône de la communauté {c.name}
                            </span>
                            <div className="w-full h-full flex items-center justify-center">
                              {iconBusyId === c.id ? (
                                <Icon
                                  name="Loader2"
                                  size={18}
                                  className="text-muted-foreground animate-spin"
                                />
                              ) : c.groupIconUrl ? (
                                <img
                                  key={`${c.groupIconUrl}-${groupIconCacheBust[c.id] ?? ''}`}
                                  src={hubupGroupIconImgSrc(
                                    c.groupIconUrl,
                                    groupIconCacheBust[c.id],
                                  )}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Icon
                                  name="ImagePlus"
                                  size={18}
                                  className="text-muted-foreground group-hover:text-primary transition-colors"
                                />
                              )}
                            </div>
                          </label>
                          {c.groupIconUrl && iconBusyId !== c.id ? (
                            <button
                              type="button"
                              title="Retirer l’icône"
                              className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:bg-destructive/15 hover:text-destructive z-10"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void handleDeleteIcon(c);
                              }}
                            >
                              <Icon name="X" size={12} strokeWidth={2.5} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground font-medium">
                        {c.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {c.isPro ? 'Oui' : 'Non'}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground tabular-nums">
                        {Math.max(0, Number(c.notificationLeft) || 0)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {c.inviteCode ? (
                          <div className="min-w-0">
                            <code className="text-xs font-mono text-foreground break-all">
                              {c.inviteCode}
                            </code>
                            <div className="flex flex-wrap items-center gap-1 mt-1.5">
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Copier le code"
                                onClick={() => void copyInviteCode(c.inviteCode)}
                              >
                                <Icon name="Copy" size={15} className="text-muted-foreground" />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth disabled:opacity-40"
                                title="Copier le lien (notification-feed)"
                                disabled={!buildHubupGroupInviteUrl(c.inviteCode)}
                                onClick={() => void copyInviteLink(c.inviteCode)}
                              >
                                <Icon name="Link" size={15} className="text-muted-foreground" />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Voir le QR code"
                                onClick={() => setQrModal(c)}
                              >
                                <Icon name="QrCode" size={15} className="text-muted-foreground" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="p-1.5 rounded hover:bg-muted transition-smooth"
                            title="Modifier"
                            onClick={() => openEdit(c)}
                          >
                            <Icon name="Pencil" size={16} className="text-muted-foreground" />
                          </button>
                          <button
                            type="button"
                            className="p-1.5 rounded hover:bg-muted transition-smooth disabled:opacity-50"
                            title="Supprimer"
                            disabled={deleteBusyId === c.id}
                            onClick={() => handleDelete(c)}
                          >
                            {deleteBusyId === c.id ? (
                              <Icon
                                name="Loader2"
                                size={16}
                                className="text-muted-foreground animate-spin"
                              />
                            ) : (
                              <Icon name="Trash2" size={16} className="text-error" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {qrModal ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-sm border border-border"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground pr-2">
                Rejoindre « {qrModal.name} »
              </h2>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-smooth shrink-0"
                onClick={() => setQrModal(null)}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {(() => {
                const inviteUrl = buildHubupGroupInviteUrl(qrModal.inviteCode);
                return inviteUrl ? (
                  <>
                    <div className="flex justify-center bg-background rounded-lg p-4 border border-border">
                      <HubupPremiumQr
                        groupId={qrModal.id}
                        inviteCode={qrModal.inviteCode}
                        groupIconUrl={qrModal.groupIconUrl}
                        faviconUrl={qrModal.faviconUrl}
                        size={180}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      iconName="Copy"
                      iconPosition="left"
                      onClick={() => void copyInviteLink(qrModal.inviteCode)}
                    >
                      Copier le lien d&apos;invitation
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Lien d&apos;invitation indisponible pour le moment. Contactez
                    l&apos;administrateur si le problème persiste.
                  </p>
                );
              })()}
              {qrModal.inviteCode ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    iconName="Copy"
                    iconPosition="left"
                    onClick={() => void copyInviteCode(qrModal.inviteCode)}
                  >
                    Copier le code
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {showForm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-md"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">
                {editing ? 'Modifier la communauté' : 'Nouvelle communauté'}
              </h2>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-smooth"
                onClick={() => setShowForm(false)}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Nom
                </label>
                <input
                  type="text"
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formName}
                  onChange={(ev) => setFormName(ev.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="hubup-community-is-pro"
                  type="checkbox"
                  checked={formIsPro}
                  onChange={(ev) => setFormIsPro(ev.target.checked)}
                  className="rounded border-input"
                />
                <label htmlFor="hubup-community-is-pro" className="text-sm text-foreground">
                  Communauté Pro
                </label>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Notifications restantes
                </label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formNotificationLeft}
                  onChange={(ev) => setFormNotificationLeft(ev.target.value)}
                />
              </div>
              {formError && (
                <p className="text-xs text-error">{formError}</p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Annuler
                </Button>
                <Button type="submit" variant="success" iconName="Save" iconPosition="left">
                  Sauvegarder
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default HubupCommunitiesManage;
